import {
  AuthProvider,
  createAuthClientFactory,
  UserClaims,
} from '@airship-platform/airship-auth';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import { Theme, ThemeProvider } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import * as Sentry from '@sentry/react';
import {
  createContext,
  PropsWithChildren,
  Suspense,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { AssetProvider } from '../features/assets/AssetContext';
import {
  setAnalyticsUser,
  setErrorCaptureUser,
  setGTMUser,
  setupAnalytics,
  setupErrorCapture,
  setupGTM,
} from '../services';
import { HttpAuthProvider } from './HttpAuthContext';
import MuiXLicense from './MuiXLicense';
import { NavigationHistoryProvider } from './NavigationHistoryContext';
import { PagePropertiesProvider } from './PagePropertiesContext';
import { ToastProvider } from './ToastContext';

const isNewVersionError = (error: unknown) =>
  error instanceof Error &&
  [
    'Failed to fetch dynamically imported module',
    `'text/html' is not a valid JavaScript MIME type.`,
  ].some((message) => error.message.startsWith(message));

const SuspenseFallback: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
    }}
  >
    <CircularProgress color="primary" />
  </Box>
);

const ErrorFallback: React.FC<{ error?: string }> = ({ error }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      width: '100vw',
      gap: 4,
    }}
  >
    <Typography variant="h2" color="error">
      Oops! Something went wrong.
    </Typography>
    <Typography variant="body1">
      {error || 'An unexpected error occurred'}
    </Typography>
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Button onClick={() => (window.location.href = '/')}>
        Go to main page
      </Button>
      <Button
        variant="outlined"
        onClick={() =>
          Sentry.showReportDialog({
            onClose: () => (window.location.href = '/'),
          })
        }
      >
        Report feedback
      </Button>
    </Box>
  </Box>
);

interface EnvironmentContextProps {
  env: string;
}

interface EnvironmentProviderProps {
  env: string;
  apiBaseUrl: string;
  theme: Theme;
  authConfig: {
    signInUrl: string;
    apiBaseUrl: string;
    basePublicFilesUrl: string;
    callbackUrl: string;
  };
  assetsConfig: {
    basePublicFilesUrl: string;
  };
  sentryConfig: Parameters<typeof setupErrorCapture>[0];
  amplitudeConfig: Parameters<typeof setupAnalytics>[0];
  gtmConfig: Parameters<typeof setupGTM>[0];
}

const EnvironmentContext = createContext<EnvironmentContextProps | undefined>(
  undefined
);

const EnvironmentProvider: React.FC<
  PropsWithChildren<EnvironmentProviderProps>
> = ({
  env,
  apiBaseUrl,
  theme,
  authConfig,
  assetsConfig,
  sentryConfig,
  amplitudeConfig,
  gtmConfig,
  children,
}) => {
  const initRef = useRef<boolean>(false);

  useEffect(() => {
    if (!initRef.current) {
      initRef.current = true;

      setupErrorCapture(sentryConfig);
      setupAnalytics(amplitudeConfig);
      setupGTM(gtmConfig);
    }
  }, [amplitudeConfig, apiBaseUrl, sentryConfig, gtmConfig]);

  const setUserCallback = (user: UserClaims) => {
    if (initRef.current) {
      setErrorCaptureUser(user);
      setAnalyticsUser(user);
      setGTMUser(user);
    }
  };

  return (
    <EnvironmentContext.Provider value={{ env }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Sentry.ErrorBoundary
          onError={(error) => {
            if (isNewVersionError(error)) {
              setTimeout(() => window.location.reload(), 1000);
            }
          }}
          fallback={({ error }) =>
            isNewVersionError(error) ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100vh',
                  width: '100vw',
                }}
              >
                <Typography variant="body1">
                  New version available. Reloading...
                </Typography>
              </Box>
            ) : (
              <ErrorFallback error={error?.toString()} />
            )
          }
        >
          <ToastProvider>
            <AuthProvider
              signInUrl={authConfig.signInUrl}
              callbackUrl={`${window.location.origin}${authConfig.callbackUrl}`}
              setUserCallback={setUserCallback}
              onUserMismatch={() => (window.location.href = '/')}
              authClient={createAuthClientFactory({
                baseUrl: authConfig.apiBaseUrl,
              })}
            >
              <AssetProvider
                basePublicFilesUrl={assetsConfig.basePublicFilesUrl}
              >
                <NavigationHistoryProvider>
                  <HttpAuthProvider options={{ apiBaseUrl }}>
                    <PagePropertiesProvider defaultPageTitle={document.title}>
                      <Suspense fallback={<SuspenseFallback />}>
                        {children}
                      </Suspense>
                      <MuiXLicense />
                    </PagePropertiesProvider>
                  </HttpAuthProvider>
                </NavigationHistoryProvider>
              </AssetProvider>
            </AuthProvider>
          </ToastProvider>
        </Sentry.ErrorBoundary>
      </ThemeProvider>
    </EnvironmentContext.Provider>
  );
};

const useEnvironmentContext = (): EnvironmentContextProps => {
  const context = useContext(EnvironmentContext);
  if (!context) {
    throw new Error(
      'useEnvironmentContext must be used within a EnvironmentProvider'
    );
  }

  return context;
};

export { EnvironmentProvider, useEnvironmentContext };
